import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface PaymentStatusModalProps {
  open: boolean;
  status: 'idle' | 'processing' | 'pending' | 'success' | 'error';
  orderId: string | null;
  errorMessage?: string;
  onClose: () => void;
  onRetry: () => void;
}

const PaymentStatusModal: React.FC<PaymentStatusModalProps> = ({
  open,
  status,
  orderId,
  errorMessage,
  onClose,
  onRetry,
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isChecking, setIsChecking] = useState(false);

  // Poll for payment status when pending
  useEffect(() => {
    if (status !== 'pending' || !orderId) return;

    const pollInterval = setInterval(async () => {
      try {
        const { data, error } = await supabase.functions.invoke('verify-revolut-order', {
          body: { order_id: orderId }
        });

        if (error) throw error;

        if (data?.status === 'active') {
          clearInterval(pollInterval);
          toast({
            title: "Payment Confirmed!",
            description: "Your subscription is now active.",
          });
          // Close modal and redirect
          setTimeout(() => {
            onClose();
            navigate('/');
          }, 2000);
        }
      } catch (err) {
        console.error('Status check error:', err);
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(pollInterval);
  }, [status, orderId, navigate, toast, onClose]);

  const handleCheckStatus = async () => {
    if (!orderId) return;

    setIsChecking(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-revolut-order', {
        body: { order_id: orderId }
      });

      if (error) throw error;

      if (data?.status === 'active') {
        toast({
          title: "Payment Confirmed!",
          description: "Your subscription is now active.",
        });
        setTimeout(() => {
          onClose();
          navigate('/');
        }, 2000);
      } else {
        toast({
          title: "Still Processing",
          description: "Your payment is being verified. Please wait a moment.",
        });
      }
    } catch (err) {
      console.error('Manual status check error:', err);
      toast({
        title: "Check Failed",
        description: "Unable to verify payment status. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsChecking(false);
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'processing':
        return (
          <div className="text-center py-8">
            <div className="relative mx-auto w-20 h-20 mb-6">
              <Loader2 className="h-20 w-20 animate-spin text-primary" />
            </div>
            <DialogTitle className="text-2xl font-bold mb-3">
              Securing Your Payment
            </DialogTitle>
            <DialogDescription className="text-base leading-relaxed max-w-sm mx-auto">
              Please wait while we process your payment securely through Revolut.
              <br />
              <span className="text-xs text-muted-foreground mt-2 block">
                This usually takes just a few seconds...
              </span>
            </DialogDescription>
          </div>
        );
      
      case 'pending':
        return (
          <div className="text-center py-8">
            <div className="relative mx-auto w-20 h-20 mb-6">
              <Loader2 className="h-20 w-20 animate-spin text-primary" />
            </div>
            <DialogTitle className="text-2xl font-bold mb-3">
              Verifying Payment
            </DialogTitle>
            <DialogDescription className="text-base leading-relaxed max-w-sm mx-auto mb-6">
              We're confirming your payment with the bank. This usually takes a few seconds...
            </DialogDescription>
            <div className="flex flex-col gap-3 max-w-xs mx-auto">
              <Button onClick={handleCheckStatus} variant="outline" size="lg" className="w-full">
                <RefreshCw className="mr-2 h-4 w-4" />
                Check Status Now
              </Button>
              <Button onClick={onClose} variant="ghost" className="w-full">
                Close
              </Button>
            </div>
          </div>
        );
      
      case 'success':
        return (
          <div className="text-center py-8">
            <div className="mx-auto w-20 h-20 mb-6 relative">
              <CheckCircle className="h-20 w-20 text-green-500 mx-auto animate-in zoom-in duration-300" />
            </div>
            <DialogTitle className="text-2xl font-bold mb-3 flex items-center justify-center gap-2">
              Payment Successful! 🎉
            </DialogTitle>
            <DialogDescription className="text-base leading-relaxed max-w-sm mx-auto mb-6">
              Your subscription is now active!
              <br />
              <span className="text-sm text-muted-foreground mt-2 block">
                Redirecting to dashboard in a moment...
              </span>
            </DialogDescription>
            <Button onClick={onClose} size="lg" className="w-full max-w-xs mx-auto">
              Go to Dashboard Now
            </Button>
          </div>
        );
      
      case 'error':
        return (
          <div className="text-center py-8">
            <div className="mx-auto w-20 h-20 mb-6">
              <XCircle className="h-20 w-20 text-red-500 mx-auto" />
            </div>
            <DialogTitle className="text-2xl font-bold mb-3">
              Payment Failed
            </DialogTitle>
            <DialogDescription className="text-base leading-relaxed max-w-sm mx-auto mb-2">
              {errorMessage || "There was an error processing your payment."}
            </DialogDescription>
            
            <div className="bg-muted/50 rounded-lg p-4 max-w-sm mx-auto mb-6 mt-4">
              <p className="text-sm font-medium mb-2">Common solutions:</p>
              <ul className="text-xs text-left space-y-1 text-muted-foreground">
                <li>• Check your card details are correct</li>
                <li>• Try a different payment method</li>
                <li>• Contact your bank if issues persist</li>
              </ul>
            </div>
            
            <div className="flex flex-col gap-3 max-w-xs mx-auto">
              <Button onClick={onRetry} size="lg" className="w-full">
                Try Again
              </Button>
              <Button onClick={onClose} variant="outline" className="w-full">
                Cancel
              </Button>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default PaymentStatusModal;
